import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

import { BTLocalStorage } from "types/btStorage";

import { isInPortal } from "utilities/portal/portal";

import { JobPickerDisplayModes } from "commonComponents/utilities/JobPicker/JobPicker.types";

import { type ScheduleDraftScheduleItem } from "handlers";

import { type GanttEntity } from "entity/schedule/Gantt/Gantt.api.types";
import {
    handleDraftValidation,
    validateDraft,
} from "entity/schedule/ScheduleDrafts/draft-validation.utility";
import {
    applyDraftChange,
    createOriginalScheduleSnapshot,
    generateScheduleDraftItemsFromGanttEntity,
    isJobDraftChange,
    updateScheduleWithDraftChanges,
} from "entity/schedule/ScheduleDrafts/draft.utility";
import {
    DraftOperations,
    type IDraftHistoryState,
} from "entity/schedule/ScheduleDrafts/ScheduleDrafts.types";
import { useDraftApi } from "entity/schedule/ScheduleDrafts/useDraftApi";
import {
    getAllDraftsForBuilder,
    useDraftStorage,
} from "entity/schedule/ScheduleDrafts/useDraftStorage";
import { ScheduleListContainerTabs } from "entity/schedule/ScheduleListContainer/ScheduleListContainer.api.types";

const MAX_DRAFT_HISTORY_SIZE = 100;

interface IUseDraftManagerParams {
    builderId: number;
    jobIds: number[];
    listEntity?: GanttEntity;
    draftModeFlag: boolean;
}

/**
 * Hook for managing draft state in the Gantt schedule system.
 *
 * Handles draft mode toggling with persistence, entity filtering based on showOnGantt property,
 * draft history management with undo/redo functionality, change persistence to IndexedDB,
 * and cross-component draft change broadcasting.
 */
export function useDraftManager({
    builderId,
    jobIds,
    listEntity,
    draftModeFlag,
}: IUseDraftManagerParams) {
    const jobId = jobIds[0];
    const [showDraftMode, setShowDraftMode] = useState(false);
    const [draftListEntity, setDraftListEntity] = useState<GanttEntity | undefined>(listEntity);
    const [draftHistory, setDraftHistory] = useState<IDraftHistoryState | null>(null);
    const prevDraftHistory = useRef<IDraftHistoryState | null>(null);

    const { setDraft, getDraft, removeDraft } = useDraftStorage(builderId, jobId);
    const { startDraftMode, endDraftMode } = useDraftApi({ jobId });

    const updateDraftHistory = useCallback((newHistory: IDraftHistoryState | null) => {
        setDraftHistory(newHistory);
        prevDraftHistory.current = newHistory;
    }, []);

    useEffect(() => {
        const storedDraftMode = BTLocalStorage.get("bt-boolean-useDraftMode") ?? false;
        setShowDraftMode(storedDraftMode && draftModeFlag);
    }, [draftModeFlag]);

    useEffect(() => {
        const prevJobId = prevDraftHistory.current?.current?.jobId;
        if (prevJobId && prevJobId !== jobId && draftHistory) {
            updateDraftHistory(null);
            fireDraftChangeEvent();
        }
    }, [jobId, draftHistory, updateDraftHistory]);

    const applyEntityFiltering = useCallback(
        (entity: GanttEntity, isDraftModeOn: boolean): GanttEntity => {
            if (!entity?.taskData) {
                return entity;
            }

            if (isDraftModeOn) {
                return entity;
            }

            const filteredData = entity.taskData.data.filter((item) => item.showOnGantt);
            return {
                ...entity,
                taskData: {
                    ...entity.taskData,
                    data: filteredData,
                },
            };
        },
        [],
    );

    const createInitialDraftState = useCallback(
        (entity: GanttEntity) => {
            const initialItems = generateScheduleDraftItemsFromGanttEntity(entity);
            const originalSnapshot = createOriginalScheduleSnapshot(entity);
            return {
                current: { jobId, currentState: { items: initialItems } },
                history: [],
                future: [],
                originalSnapshot,
            };
        },
        [jobId],
    );

    const loadDraftIfExists = useCallback(
        async (entity: GanttEntity): Promise<GanttEntity> => {
            const stored = await getDraft();
            if (stored && (stored.future.length > 0 || stored.history.length > 0)) {
                const originalSnapshot =
                    stored.originalSnapshot || createOriginalScheduleSnapshot(entity);
                const draftItems = stored.current.currentState.items;

                const isDraftValid = validateDraft(originalSnapshot, entity, draftItems);

                if (!isDraftValid) {
                    await handleDraftValidation(isDraftValid);
                    await removeDraft();
                    updateDraftHistory(null);
                    fireDraftChangeEvent();
                    return entity;
                }

                const validatedStoredDraft = {
                    ...stored,
                    originalSnapshot,
                };

                if (!stored.originalSnapshot) {
                    await setDraft(validatedStoredDraft);
                }

                updateDraftHistory(validatedStoredDraft);
                return updateScheduleWithDraftChanges(entity, stored.current);
            } else {
                if (draftHistory) {
                    updateDraftHistory(null);
                    fireDraftChangeEvent();
                } else {
                    const currentDraftHistory = createInitialDraftState(entity);
                    updateDraftHistory(currentDraftHistory);
                }
            }
            return entity;
        },
        [
            getDraft,
            updateDraftHistory,
            removeDraft,
            setDraft,
            draftHistory,
            createInitialDraftState,
        ],
    );

    const setDraftListEntityWithFiltering = useCallback(
        (entity: GanttEntity, isDraftModeOn: boolean) => {
            const filteredEntity = applyEntityFiltering(entity, isDraftModeOn);
            setDraftListEntity(filteredEntity);
        },
        [applyEntityFiltering],
    );

    const persistDraft = useCallback(
        async (newState: IDraftHistoryState) => {
            if (newState.future.length > 0 || newState.history.length > 0) {
                await setDraft(newState);
            } else {
                await removeDraft();
            }
        },
        [removeDraft, setDraft],
    );

    const refreshIfNeeded = useCallback(async (newHistory: IDraftHistoryState | null) => {
        if (isJobDraftChange(prevDraftHistory.current, newHistory)) {
            fireDraftChangeEvent();
        }
    }, []);

    const applyChange = useCallback(
        async (change: ScheduleDraftScheduleItem) => {
            if (!draftListEntity) {
                return;
            }

            let currentDraftHistory = draftHistory;
            if (!currentDraftHistory) {
                currentDraftHistory = createInitialDraftState(draftListEntity);
                updateDraftHistory(currentDraftHistory);
            }

            // Start draft mode on first change
            if (currentDraftHistory.history.length === 0) {
                await startDraftMode();
            }

            const { newDraftState, newListEntity } = await applyDraftChange(
                draftListEntity,
                currentDraftHistory.current,
                change,
            );

            const originalSnapshot = currentDraftHistory.originalSnapshot;

            const newHistory = [...currentDraftHistory.history, currentDraftHistory.current];
            const limitedHistory =
                newHistory.length > MAX_DRAFT_HISTORY_SIZE
                    ? newHistory.slice(-MAX_DRAFT_HISTORY_SIZE)
                    : newHistory;

            const newState = {
                current: newDraftState,
                history: limitedHistory,
                future: [],
                originalSnapshot,
            };
            await persistDraft(newState);
            await refreshIfNeeded(newState);
            updateDraftHistory(newState);
            setDraftListEntityWithFiltering(newListEntity, showDraftMode);
        },
        [
            createInitialDraftState,
            draftListEntity,
            draftHistory,
            persistDraft,
            refreshIfNeeded,
            setDraftListEntityWithFiltering,
            showDraftMode,
            startDraftMode,
            updateDraftHistory,
        ],
    );

    const undoDraftChanges = useCallback(async (): Promise<number | undefined> => {
        if (!draftHistory || !draftListEntity || draftHistory.history.length === 0) {
            return undefined;
        }
        const { history, future, current } = draftHistory;
        const prev = history[history.length - 1];

        const sourceItemId = current.sourceItemId;

        const newState = {
            current: prev,
            history: history.slice(0, -1),
            future: [current, ...future],
            originalSnapshot: draftHistory.originalSnapshot,
        };
        await persistDraft(newState);
        updateDraftHistory(newState);
        const updated = updateScheduleWithDraftChanges(draftListEntity, prev);
        setDraftListEntityWithFiltering(updated, showDraftMode);

        return sourceItemId ?? undefined;
    }, [
        draftHistory,
        draftListEntity,
        persistDraft,
        setDraftListEntityWithFiltering,
        showDraftMode,
        updateDraftHistory,
    ]);

    const redoDraftChanges = useCallback(async (): Promise<number | undefined> => {
        if (!draftHistory || !draftListEntity || draftHistory.future.length === 0) {
            return undefined;
        }
        const { history, future, current } = draftHistory;
        const next = future[0];

        const sourceItemId = next.sourceItemId;

        const newHistory = [...history, current];
        const limitedHistory =
            newHistory.length > MAX_DRAFT_HISTORY_SIZE
                ? newHistory.slice(-MAX_DRAFT_HISTORY_SIZE)
                : newHistory;

        const newState = {
            current: next,
            history: limitedHistory,
            future: future.slice(1),
            originalSnapshot: draftHistory.originalSnapshot,
        };
        await persistDraft(newState);
        updateDraftHistory(newState);
        const updated = updateScheduleWithDraftChanges(draftListEntity, next);
        setDraftListEntityWithFiltering(updated, showDraftMode);

        return sourceItemId ?? undefined;
    }, [
        draftHistory,
        draftListEntity,
        persistDraft,
        setDraftListEntityWithFiltering,
        showDraftMode,
        updateDraftHistory,
    ]);

    const discardDraft = useCallback(
        async (entity: GanttEntity) => {
            updateDraftHistory(null);
            await removeDraft();
            await endDraftMode();
            fireDraftChangeEvent();
            setDraftListEntityWithFiltering(entity, showDraftMode);
            const currentDraftHistory = createInitialDraftState(entity);
            updateDraftHistory(currentDraftHistory);
            return entity;
        },
        [
            updateDraftHistory,
            removeDraft,
            endDraftMode,
            setDraftListEntityWithFiltering,
            showDraftMode,
            createInitialDraftState,
        ],
    );

    const toggleDraftMode = useCallback(
        async (entity: GanttEntity, enableDraftMode: boolean) => {
            setShowDraftMode(enableDraftMode);
            BTLocalStorage.set("bt-boolean-useDraftMode", enableDraftMode);

            if (enableDraftMode) {
                const entityWithDraft = await loadDraftIfExists(entity);
                setDraftListEntityWithFiltering(entityWithDraft, true);
                return entityWithDraft;
            } else {
                updateDraftHistory(null);
                setDraftListEntityWithFiltering(entity, false);
                return entity;
            }
        },
        [loadDraftIfExists, setDraftListEntityWithFiltering, updateDraftHistory],
    );

    const refreshDraft = useCallback(
        async (entity: GanttEntity) => {
            const useDraftMode = BTLocalStorage.get("bt-boolean-useDraftMode") ?? false;
            setShowDraftMode(useDraftMode);

            if (useDraftMode) {
                const entityWithDraft = await loadDraftIfExists(entity);
                setDraftListEntityWithFiltering(entityWithDraft, true);
                return entityWithDraft;
            } else {
                setDraftListEntityWithFiltering(entity, false);
                return entity;
            }
        },
        [loadDraftIfExists, setDraftListEntityWithFiltering],
    );

    const handleDraftOperation = useCallback(
        async (entity: GanttEntity, draftOperation: DraftOperations): Promise<GanttEntity> => {
            switch (draftOperation) {
                case DraftOperations.Discard:
                    return await discardDraft(entity);
                case DraftOperations.Refresh:
                    return await refreshDraft(entity);
                case DraftOperations.EnableDraftMode:
                    return await toggleDraftMode(entity, true);
                case DraftOperations.DisableDraftMode:
                    return await toggleDraftMode(entity, false);
                default:
                    throw new Error(`Invalid draft operation: ${draftOperation}`);
            }
        },
        [discardDraft, refreshDraft, toggleDraftMode],
    );

    return {
        showDraftMode,
        draftHistory,
        applyChange,
        undoDraftChanges,
        redoDraftChanges,
        handleDraftOperation,
        draftListEntity,
    };
}

const DRAFT_CHANGE_CHANNEL = "scheduleDraftChange";

function useDraftChangeListener(callback: () => void) {
    useEffect(() => {
        const channel = new BroadcastChannel(DRAFT_CHANGE_CHANNEL);

        channel.addEventListener("message", callback);

        return () => {
            channel.removeEventListener("message", callback);
            channel.close();
        };
    }, [callback]);
}

export function fireDraftChangeEvent() {
    const channel = new BroadcastChannel(DRAFT_CHANGE_CHANNEL);
    channel.postMessage({ type: "draftChange" });
    channel.close();
}

export function useScheduledDraftsForJobPicker(
    builderId: number,
    displayMode: JobPickerDisplayModes,
) {
    const location = useLocation();
    const [draftJobIds, setDraftJobIds] = useState<number[]>([]);
    const [isDraftModeEnabled, setIsDraftModeEnabled] = useState(
        BTLocalStorage.get("bt-boolean-useDraftMode") ?? false,
    );

    const shouldDisplayDrafts = useMemo(() => {
        const isOnGanttTab = location.pathname.includes(
            `/Schedules/${ScheduleListContainerTabs.Gantt}`,
        );

        return (
            isInPortal({ builder: true }) &&
            displayMode === JobPickerDisplayModes.Calendar &&
            isOnGanttTab &&
            isDraftModeEnabled
        );
    }, [displayMode, isDraftModeEnabled, location.pathname]);

    const updateDraftJobIds = useCallback(async () => {
        if (shouldDisplayDrafts) {
            const drafts = await getAllDraftsForBuilder(builderId);
            const jobIds = drafts
                .map((draft) => draft.jobId)
                .filter((id): id is number => id !== undefined);
            setDraftJobIds(jobIds);
        } else {
            setDraftJobIds([]);
        }
    }, [shouldDisplayDrafts, builderId]);

    // Listen for changes to draft mode setting
    useEffect(() => {
        const handleDraftModeChange = (oldValue: boolean | null, newValue: boolean | null) => {
            setIsDraftModeEnabled(newValue ?? false);
        };

        BTLocalStorage.addChangeListener("bt-boolean-useDraftMode", handleDraftModeChange);

        return () => {
            BTLocalStorage.removeChangeListener("bt-boolean-useDraftMode", handleDraftModeChange);
        };
    }, []);

    useDraftChangeListener(updateDraftJobIds);

    useEffect(() => {
        void updateDraftJobIds();
    }, [updateDraftJobIds]);

    return draftJobIds;
}
